import { useState, useEffect } from "react";
import { useEmailAPI, EmailMessage } from "../hooks/useEmailAPI";
import { useAuth } from "../hooks/useAuth";
import UserProfile from "../components/UserProfile";
import FolderList from "../components/dashboard/FolderList";
import EmailList from "../components/dashboard/EmailList";
import EmailDetail from "../components/dashboard/EmailDetail";

function Dashboard() {
  const { user } = useAuth();
  const { emails, folders, loading, error, fetchEmails, fetchFolders } =
    useEmailAPI();

  const [selectedFolder, setSelectedFolder] = useState<string>("INBOX");
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);

  // Load emails when selected folder changes
  useEffect(() => {
    const loadEmails = async () => {
      try {
        const result = await fetchEmails(selectedFolder);
        // This might cause re-renders if it changes on every load
        if (result.length > 0 && !selectedEmail) {
          setSelectedEmail(result[0]);
        }
      } catch (err) {
        console.error("Failed to load emails:", err);
      }
    };

    loadEmails();
  }, [selectedFolder, fetchEmails]); // fetchEmails is a dependency

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolder(folderId);
    setSelectedEmail(null);
  };

  const handleEmailSelect = (email: EmailMessage) => {
    setSelectedEmail(email);
  };

  return (
    <div className="space-y-6">
      {/* User welcome section */}
      <div className="bg-white rounded-lg p-6 border mb-6">
        <UserProfile />
      </div>

      {error ? (
        <div className="p-6 bg-red-50 border border-red-100 rounded-lg text-red-500">
          <h3 className="font-medium mb-2">Error loading emails</h3>
          <p>{error}</p>
        </div>
      ) : (
        <div>
          <h2 className="text-2xl font-bold text-[#694e42] mb-4">Your Email</h2>

          {/* Email dashboard */}
          <div className="grid grid-cols-12 gap-4">
            {/* Left sidebar - folders */}
            <div className="col-span-12 md:col-span-3 lg:col-span-2">
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="font-bold text-[#694e42] mb-3">Folders</h3>
                <FolderList
                  folders={folders}
                  loading={loading}
                  selectedFolderId={selectedFolder}
                  onSelectFolder={handleFolderSelect}
                />
              </div>
            </div>

            {/* Middle column - email list */}
            <div className="col-span-12 md:col-span-9 lg:col-span-4">
              <EmailList
                emails={emails}
                loading={loading}
                onSelectEmail={handleEmailSelect}
              />
            </div>

            {/* Right column - email detail */}
            <div className="col-span-12 lg:col-span-6 hidden lg:block">
              <EmailDetail email={selectedEmail} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
